import { useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, Info, ShieldAlert } from "lucide-react";
import { cn } from "@wa/ui";
import { sortFindings, type Finding, type FindingSeverity } from "../../../lib/siteScan";
import { Card, CardHead, StatusPill } from "../../ui";
import { SEVERITY_COLOUR } from "./severity";
import type { T } from "./types";

/**
 * Findings, worst first.
 *
 * Each row is one sentence an operator can say out loud, which is why the id is
 * also the translation key: adding a check on the API means adding a sentence
 * here and nothing else. The positive findings are collapsed by default - they
 * exist so a call can open on something true and friendly, not to pad the list.
 *
 * Severity used to be four hand-written Tailwind pairs in this file. A row is a
 * verdict on the site, which is what a status pill says everywhere else in the
 * CRM, so it takes the theme's colours now - see `severity.ts` for why the
 * scanner keeps its own palette for score bands and version gaps but not for
 * this.
 */

const ICON: Record<FindingSeverity, typeof Info> = {
  critical: ShieldAlert,
  warning: AlertTriangle,
  info: Info,
  good: CheckCircle2,
};

export function FindingsList({ findings, t }: { findings: Finding[]; t: T }) {
  const [showGood, setShowGood] = useState(false);
  const sorted = sortFindings(findings);
  const issues = sorted.filter((f) => f.severity !== "good");
  const good = sorted.filter((f) => f.severity === "good");

  const counts = {
    critical: issues.filter((f) => f.severity === "critical").length,
    warning: issues.filter((f) => f.severity === "warning").length,
    info: issues.filter((f) => f.severity === "info").length,
  };

  return (
    <Card>
      <CardHead
        title={
          <span className="flex items-center gap-2">
            <ShieldAlert className="size-4 text-primary" /> {t("scanner.findings.title")}
          </span>
        }
        actions={
          <span className="flex flex-wrap items-center gap-1.5">
            {counts.critical > 0 && (
              <StatusPill tone="lt">
                {t("scanner.severity.critical")} {counts.critical}
              </StatusPill>
            )}
            {counts.warning > 0 && (
              <StatusPill tone="wt">
                {t("scanner.severity.warning")} {counts.warning}
              </StatusPill>
            )}
            {counts.info > 0 && (
              <StatusPill tone="mut">
                {t("scanner.severity.info")} {counts.info}
              </StatusPill>
            )}
          </span>
        }
      />

      <div className="crm-scan-body">
        {issues.length === 0 ? (
          <p className="flex items-center gap-2 text-sm" style={{ color: "var(--color-success)" }}>
            <CheckCircle2 className="size-4" /> {t("scanner.findings.clean")}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {issues.map((f) => (
              <FindingRow key={f.id} finding={f} t={t} />
            ))}
          </ul>
        )}

        {good.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setShowGood((v) => !v)}
              aria-expanded={showGood}
              className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
            >
              <ChevronDown className={cn("size-3.5 transition-transform", showGood && "rotate-180")} />
              {t("scanner.findings.goodToggle", { count: good.length })}
            </button>
            {showGood && (
              <ul className="mt-2 space-y-1.5">
                {good.map((f) => (
                  <FindingRow key={f.id} finding={f} t={t} />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * One finding.
 *
 * A sunken row with a coloured rule down its left edge rather than a tinted box
 * with a tinted border. Four background tints stacked in one card turned the
 * list into a gradient; the rule says the same thing in the width the eye reads
 * a list by, and an `info` row gets a muted rule so it stays quiet.
 */
function FindingRow({ finding, t }: { finding: Finding; t: T }) {
  const Icon = ICON[finding.severity];
  const colour = SEVERITY_COLOUR[finding.severity];
  return (
    <li
      className="flex items-start gap-2.5 rounded-2xl bg-[var(--crm-sunk)] px-3.5 py-2.5"
      style={{ boxShadow: `inset 3px 0 0 0 color-mix(in oklab, ${colour} 65%, transparent)` }}
    >
      <Icon className="mt-0.5 size-4 shrink-0" style={{ color: colour }} />
      <span className="text-sm leading-snug">{t(`scanner.finding.${finding.id}`, finding.vars)}</span>
    </li>
  );
}
